import type { ColorAnimationSpace, AnimationProperty } from "@/document/model/animation";
import {
  hsbToOklch,
  hslToOklch,
  oklchColor,
  oklchToHsb,
  oklchToHsl,
  oklchToRgb,
  parseColor,
  rgbToOklch,
  type HsbColor,
  type HslColor,
  type OklchColor,
  type RgbColor,
} from "@/document/model/color";

export const COLOR_ANIMATION_CHANNELS: Record<ColorAnimationSpace, Array<{ key: string; label: string }>> = {
  oklch: [{ key: "l", label: "L" }, { key: "c", label: "C" }, { key: "h", label: "H" }, { key: "alpha", label: "A" }],
  hsl: [{ key: "h", label: "H" }, { key: "s", label: "S" }, { key: "l", label: "L" }, { key: "alpha", label: "A" }],
  rgb: [{ key: "r", label: "R" }, { key: "g", label: "G" }, { key: "b", label: "B" }, { key: "alpha", label: "A" }],
  hsb: [{ key: "h", label: "H" }, { key: "s", label: "S" }, { key: "b", label: "B" }, { key: "alpha", label: "A" }],
};

export interface ColorAnimationChannelField {
  key: string;
  label: string;
  value: number;
  min: number;
  max: number;
  step?: number;
}

type ColorAnimationProperty = Extract<AnimationProperty, { kind: "fillColor" | "strokeColor" | "effectColor" }>;

export function isColorAnimationProperty(property: AnimationProperty): property is ColorAnimationProperty {
  return property.kind === "fillColor" || property.kind === "strokeColor" || property.kind === "effectColor";
}

export function allColorAnimationChannels(colorSpace: ColorAnimationSpace): string[] {
  return COLOR_ANIMATION_CHANNELS[colorSpace].map(channel => channel.key);
}

export function colorAnimationChannelFields(colorSpace: ColorAnimationSpace, color: OklchColor): ColorAnimationChannelField[] {
  return COLOR_ANIMATION_CHANNELS[colorSpace].map(channel => ({
    ...channel,
    ...colorAnimationChannelSpec(colorSpace, channel.key),
    value: readColorAnimationChannelDisplayValue(colorSpace, color, channel.key),
  }));
}

export function readColorAnimationChannelDisplayValue(colorSpace: ColorAnimationSpace, color: OklchColor, channel: string): number {
  const parsed = parseColor(color);
  switch (colorSpace) {
    case "rgb": {
      const rgb = oklchToRgb(parsed);
      if (channel === "r") return rgb.r;
      if (channel === "g") return rgb.g;
      if (channel === "b") return rgb.b;
      if (channel === "alpha") return rgb.alpha * 100;
      break;
    }
    case "hsl": {
      const hsl = oklchToHsl(parsed);
      if (channel === "h") return hsl.h;
      if (channel === "s") return hsl.s * 100;
      if (channel === "l") return hsl.l * 100;
      if (channel === "alpha") return hsl.alpha * 100;
      break;
    }
    case "hsb": {
      const hsb = oklchToHsb(parsed);
      if (channel === "h") return hsb.h;
      if (channel === "s") return hsb.s * 100;
      if (channel === "b") return hsb.b * 100;
      if (channel === "alpha") return hsb.alpha * 100;
      break;
    }
    case "oklch":
    default: {
      if (channel === "l") return parsed.l * 100;
      if (channel === "c") return parsed.c;
      if (channel === "h") return parsed.h;
      if (channel === "alpha") return parsed.alpha * 100;
      break;
    }
  }
  return 0;
}

export function writeColorAnimationChannelDisplayValue(
  colorSpace: ColorAnimationSpace,
  color: OklchColor,
  channel: string,
  displayValue: number,
): OklchColor {
  const parsed = parseColor(color);
  switch (colorSpace) {
    case "rgb": {
      const rgb = oklchToRgb(parsed);
      const next: RgbColor = channel === "r"
        ? { ...rgb, r: clamp(Math.round(displayValue), 0, 255) }
        : channel === "g"
          ? { ...rgb, g: clamp(Math.round(displayValue), 0, 255) }
          : channel === "b"
            ? { ...rgb, b: clamp(Math.round(displayValue), 0, 255) }
            : channel === "alpha"
              ? { ...rgb, alpha: clamp01(displayValue / 100) }
              : rgb;
      return rgbToOklch(next);
    }
    case "hsl": {
      const hsl = oklchToHsl(parsed);
      const next: HslColor = channel === "h"
        ? { ...hsl, h: clamp(displayValue, 0, 360) }
        : channel === "s"
          ? { ...hsl, s: clamp01(displayValue / 100) }
          : channel === "l"
            ? { ...hsl, l: clamp01(displayValue / 100) }
            : channel === "alpha"
              ? { ...hsl, alpha: clamp01(displayValue / 100) }
              : hsl;
      return hslToOklch(next);
    }
    case "hsb": {
      const hsb = oklchToHsb(parsed);
      const next: HsbColor = channel === "h"
        ? { ...hsb, h: clamp(displayValue, 0, 360) }
        : channel === "s"
          ? { ...hsb, s: clamp01(displayValue / 100) }
          : channel === "b"
            ? { ...hsb, b: clamp01(displayValue / 100) }
            : channel === "alpha"
              ? { ...hsb, alpha: clamp01(displayValue / 100) }
              : hsb;
      return hsbToOklch(next);
    }
    case "oklch":
    default: {
      if (channel === "l") return oklchColor(clamp01(displayValue / 100), parsed.c, parsed.h, parsed.alpha);
      if (channel === "c") return oklchColor(parsed.l, clamp(displayValue, 0, 0.5), parsed.h, parsed.alpha);
      if (channel === "h") return oklchColor(parsed.l, parsed.c, clamp(displayValue, 0, 360), parsed.alpha);
      if (channel === "alpha") return oklchColor(parsed.l, parsed.c, parsed.h, clamp01(displayValue / 100));
      return parsed;
    }
  }
}

export function animationPropertyWithColorChannelValue(
  property: ColorAnimationProperty,
  channel: string,
  displayValue: number,
): ColorAnimationProperty {
  return {
    ...property,
    color: writeColorAnimationChannelDisplayValue(property.colorSpace, property.color, channel, displayValue),
    channels: allColorAnimationChannels(property.colorSpace),
  };
}

export function animationPropertyWithColorSpace(
  property: ColorAnimationProperty,
  colorSpace: ColorAnimationSpace,
): ColorAnimationProperty {
  return {
    ...property,
    colorSpace,
    channels: allColorAnimationChannels(colorSpace),
  };
}

function colorAnimationChannelSpec(colorSpace: ColorAnimationSpace, channel: string): Pick<ColorAnimationChannelField, "min" | "max" | "step"> {
  if (channel === "alpha") {
    return { min: 0, max: 100, step: 1 };
  }
  if (colorSpace === "rgb") {
    return { min: 0, max: 255, step: 1 };
  }
  if (channel === "h") {
    return { min: 0, max: 360, step: 1 };
  }
  if (colorSpace === "oklch" && channel === "c") {
    return { min: 0, max: 0.5, step: 0.001 };
  }
  if (colorSpace === "oklch" && channel === "l") {
    return { min: 0, max: 100, step: 1 };
  }
  return { min: 0, max: 100, step: 1 };
}

function clamp(value: number, min: number, max: number): number {
  return Math.min(max, Math.max(min, Number.isFinite(value) ? value : min));
}

function clamp01(value: number): number {
  return clamp(value, 0, 1);
}
